import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
// Timeline components replaced with simple implementation
import {
  Print as PrintIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface PrescriptionItem {
  productName: string;
  dose: number;
  unit: string;
  frequency: string;
  durationDays: number;
  qty: number;
  route: string;
  notes?: string;
}

interface Prescription {
  id: string;
  prescriptionNumber: string;
  patientId: string;
  patientName: string;
  patientSpecies: string;
  patientWeight: number;
  clientName: string;
  doctorId: string;
  doctorName: string;
  visitId?: string;
  items: PrescriptionItem[];
  status: 'draft' | 'active' | 'signed' | 'cancelled';
  createdAt: string;
  signedAt?: string;
  dispensedAt?: string;
  revokedAt?: string;
  validUntil: string;
  notes?: string;
  instructions?: string;
}

interface PrescriptionViewProps {
  prescription: Prescription;
  onPrint: () => void;
}

const PrescriptionView: React.FC<PrescriptionViewProps> = ({
  prescription,
  onPrint
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'active': return 'primary';
      case 'signed': return 'primary';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'active': return 'Activa';
      case 'signed': return 'Semnata';
      case 'cancelled': return 'Anulata';
      default: return status;
    }
  };

  const getFrequencyText = (frequency: string): string => {
    const texts: { [key: string]: string } = {
      'qd': 'o data pe zi',
      'bid': 'de 2 ori pe zi',
      'tid': 'de 3 ori pe zi',
      'qid': 'de 4 ori pe zi',
      'q12h': 'la 12 ore',
      'q8h': 'la 8 ore',
      'q6h': 'la 6 ore',
      'prn': 'la nevoie'
    };
    return texts[frequency] || frequency;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Reteta #{prescription.prescriptionNumber}
          </Typography>
          <Chip 
            label={getStatusLabel(prescription.status)} 
            color={getStatusColor(prescription.status) as any}
            size="medium"
          />
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={onPrint}
          >
            Printeaza
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 3 }}>
        {/* Main Content */}
        <Box>
          {/* Detalii */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalii
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pacient
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {prescription.patientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {prescription.patientSpecies} • {prescription.patientWeight} kg
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Proprietar
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {prescription.clientName}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Doctor
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {prescription.doctorName}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data crearii
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDateOnly(prescription.createdAt)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Valabila pana la
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDateOnly(prescription.validUntil)}
                  </Typography>
                </Box>
                
                {prescription.visitId && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Vizita
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      #{prescription.visitId}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Produse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Produse Prescrise
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produs</TableCell>
                      <TableCell>Doza</TableCell>
                      <TableCell>Frecventa</TableCell>
                      <TableCell>Durata</TableCell>
                      <TableCell>Cantitate</TableCell>
                      <TableCell>Administrare</TableCell>
                      <TableCell>Observatii</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescription.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.dose} {item.unit}
                        </TableCell>
                        <TableCell>
                          {getFrequencyText(item.frequency)}
                        </TableCell>
                        <TableCell>
                          {item.durationDays} zile
                        </TableCell>
                        <TableCell>
                          {item.qty} {item.unit}
                        </TableCell>
                        <TableCell>
                          {item.route}
                        </TableCell>
                        <TableCell>
                          {item.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Instructiuni */}
          {prescription.instructions && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instructiuni Pacient
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {prescription.instructions}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Timeline */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Istoric Actiuni
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                    <EditIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Creata
                    </Typography>
                    <Typography variant="body2">
                      {formatForDisplay(prescription.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                
                {prescription.signedAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.light', width: 32, height: 32 }}>
                      <CheckIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Semnata
                      </Typography>
                      <Typography variant="body2">
                        {formatForDisplay(prescription.signedAt)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default PrescriptionView;
